import React, { Fragment } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { Button } from "components/ui";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useLeadDetail } from "contexts/LeadDetailContext";
import LeadDetails from "app/pages/leads/details";

const GlobalLeadDetailModal = () => {
    const { isOpen, leadId, entityName, closeLeadDetail, additionalProps } = useLeadDetail();

    // If no leadId is set (even if open), render nothing or handle gracefully.
    // The context handles clearing leadId after animation, so we check for open or leadId presence.

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 z-[200]" onClose={() => { /* Optional: close on Click Outside */ }}>
                {/* Override z-index for LeadRightSidebar when in modal */}
                <style>{`
          /* LeadRightSidebar backdrop and panel */
          .fixed.inset-0.z-60 { 
            z-index: 249 !important; 
            backdrop-filter: blur(8px) !important;
            background: rgba(0, 0, 0, 0.4) !important;
          }
          [data-sidebar="true"].z-61 { z-index: 250 !important; }
          
          /* All nested modals from lead actions should appear above */
          .fixed.inset-0[style*="z-index: 99998"] { 
            z-index: 251 !important; 
            backdrop-filter: blur(4px) !important;
          }
          .fixed.inset-0[style*="z-index: 99999"] { 
            z-index: 252 !important; 
            backdrop-filter: blur(4px) !important;
          }
          [style*="z-index: 99999"] { z-index: 252 !important; }
          
          /* Stage change modal and other lead action modals */
          .fixed.inset-0.bg-gray-900\\/50 { 
            z-index: 251 !important; 
            backdrop-filter: blur(4px) !important;
          }
          .fixed.inset-y-0.right-0 { z-index: 252 !important; }
          
          /* Any dialog or modal from lead actions */
          [role="dialog"] { z-index: 252 !important; }
          
          /* Ensure all high z-index elements from lead actions appear above */
          .z-\\[9999\\] { z-index: 252 !important; }
          .z-\\[99999\\] { z-index: 252 !important; }
        `}</style>

                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                </TransitionChild>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel
                                className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-[99vw] h-[96vh] overflow-hidden flex flex-col"
                            >
                                {/* Header */}
                                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                                    <div>
                                        <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                                            {entityName} Details
                                        </DialogTitle>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                            Complete {entityName.toLowerCase()} information and management
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={closeLeadDetail}
                                            variant="flat"
                                            isIcon
                                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2"
                                            title="Close modal"
                                        >
                                            <XMarkIcon className="w-5 h-5" />
                                        </Button>
                                    </div>
                                </div>

                                {/* Content - Uses the same LeadDetails component as the page */}
                                <div className="flex-1 overflow-hidden">
                                    {leadId && (
                                        <div className="h-full overflow-y-auto">
                                            <LeadDetails
                                                leadIdProp={leadId}
                                                isModal={true}
                                                entityName={entityName}
                                                onClose={closeLeadDetail}
                                                {...additionalProps}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-700">
                                    <Button
                                        onClick={closeLeadDetail}
                                        variant="outlined"
                                    >
                                        Close
                                    </Button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default GlobalLeadDetailModal;
